import React, { useEffect, useMemo, useRef } from 'react';

import { Typography } from 'antd';
// import { Typography } from 'antd';
// eslint-disable-next-line import/no-unresolved
import EasyVirtualized from 'easy-react-virtualized';

import AnalyticsEventsAPI from '../../../api/AnalyticsEventsAPI';
import CommunityAPI from '../../../api/CommunityAPI';
import {
  getChannelPosts,
  onRefresh,
  setCommunityViewedPostIds,
  setMetadata,
  updateCommunitPost,
} from '../../../context/communityReducer';
import {
  POST_MIN_VIEW_THRESHOLD,
  POST_MIN_VIEW_TIME,
} from '../../../shared/constants';
import { useAppDispatch, useAppSelector } from '../../../shared/hooks';
import { IChannelPostDetails } from '../../../types/communityTypes';
import { PlatformIntegrationPlatformEnum } from '../../../types/integrationType';
import NoDataFound from '../../Feed/components/NoDataFound/NoDataFound';
import PostSkeleton from '../../Feed/Posts/PostSkeleton';
import CommunityPostItem from './CommunityPostItems/CommunityPostItem';

type Props = {
  noPostsMessage?: string;
  userId?: string;
};

const noPostsMessageStyle: React.CSSProperties = {
  width: '100%',
  textAlign: 'center',
  margin: '20px 0',
};

const CommunityChannelPostList: React.FC<Props> = ({
  noPostsMessage,
  userId,
}) => {
  // console.log('this component load');

  // const userDetails = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();
  // const isMe = true;
  const userDetails = useAppSelector((state) => state.user);
  const {
    communityPostState,
    showing,
    sortBy,
    communityViewedPostIds,
    selectedChannelData,
    metadata,
    lastPostId,
  } = useAppSelector((state) => state.community);

  const channelId = useMemo(() => {
    return selectedChannelData?._id;
  }, [selectedChannelData?._id]);

  const channelIdRef = useRef<string | null>(null);
  const observer = useRef<IntersectionObserver | null>(null);
  const visibilityTimers = useRef<Map<string, number>>(new Map());

  const increaseAndUpdatePostViews = async (
    postId: string,
    post: IChannelPostDetails,
    needToIncreaseUnreadCount: boolean = true,
  ) => {
    if (!channelIdRef.current) return;
    try {
      const resp = await CommunityAPI.increasePostViews(
        channelIdRef.current as string,
        postId,
      );
      if (resp.status === 201) {
        if (needToIncreaseUnreadCount) {
          dispatch(
            updateCommunitPost({
              ...post,
              totalViews: (post?.totalViews || 0) + 1,
            }),
          );
          try {
            AnalyticsEventsAPI.trackCommunityEvents('POST_VIEWED', {
              user_id: userDetails.id,
              onboarding: userDetails.type,
              name: userDetails.name,
              email: userDetails.email,
              phone_number: userDetails.phone,
              host: userDetails.host,
              platform: PlatformIntegrationPlatformEnum.MOBILE,
              post_creator_id: post.postedBy,
              post_id: postId,
              timestamp: new Date().toISOString(),
            });
          } catch (error: any) {
            console.log('Error in tracking post viewed events:', error);
          }
          // add to viewed set to avoid multiple view count increase
          dispatch(setCommunityViewedPostIds(postId));
        }
      }
    } catch (error) {
      console.log('Error while increasing post views', error);
    }
  };

  const handlePostView = (postId: string) => {
    const post = communityPostState.posts.find((item) => item._id === postId);
    if (
      post &&
      !communityViewedPostIds.includes(postId) &&
      post?.post?.postedBy?._id !== userDetails.id
    ) {
      console.log('Post viewed:', postId);
      increaseAndUpdatePostViews(postId, post);
    }
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]) => {
    const now = Date.now();
    entries.forEach((entry) => {
      const postId = entry.target.getAttribute('data-post-id');
      if (!postId) return;

      if (entry.isIntersecting) {
        // console.log('Post visible:', postId);
        // Post is visible, add or update its visibility start time
        if (!visibilityTimers.current.has(postId)) {
          visibilityTimers.current.set(postId, now);
        }
      } else {
        // Post is no longer visible, check if it was visible long enough
        const startTime = visibilityTimers.current.get(postId);

        if (startTime) {
          const visibilityDuration = now - startTime;

          // console.log('Post hidden:', postId, startTime);

          if (visibilityDuration >= POST_MIN_VIEW_TIME) {
            handlePostView(postId);
          }
        }

        visibilityTimers.current.delete(postId);
      }
    });
  };

  const loadMore = async () => {
    // console.log(postState);
    if (communityPostState.hasMore) {
      if (userId) {
        dispatch(
          getChannelPosts({
            pageCount: communityPostState.page,
            postList: communityPostState.posts,
            showUserPosts: true,
            userId,
          }),
        );
      } else {
        dispatch(
          getChannelPosts({
            pageCount: communityPostState.page,
            postList: communityPostState.posts,
          }),
        );
      }
    }
  };

  useEffect(() => {
    dispatch(onRefresh());
    // dispatch(setCommunityPostState(initialPostState));
  }, [dispatch, channelId, showing, sortBy]);

  useEffect(() => {
    const postElements = document.querySelectorAll('.community-post-item');
    postElements.forEach((el) => observer?.current?.observe(el));

    return () => {
      postElements.forEach((el) => observer?.current?.unobserve(el));
    };
  }, [communityPostState.posts, communityViewedPostIds]);

  useEffect(() => {
    observer.current = new IntersectionObserver(handleIntersection, {
      threshold: POST_MIN_VIEW_THRESHOLD,
    });

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityPostState.posts, communityViewedPostIds]);

  useEffect(() => {
    if (typeof channelId === 'string') {
      channelIdRef.current = channelId;
    }
  }, [channelId]);

  useEffect(() => {
    if (
      channelId &&
      communityPostState?.posts?.length > 0 &&
      metadata[channelId]?.feedUnreadCount > 0
    ) {
      const newMetadata = {
        ...metadata,
        [channelId]: {
          ...metadata[channelId],
          feedUnreadCount: 0,
        },
      };
      dispatch(setMetadata(newMetadata));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    metadata,
    channelId,
    communityPostState.posts,
    communityPostState.page,
    dispatch,
  ]);

  useEffect(() => {
    if (lastPostId && channelId && metadata[channelId]?.feedUnreadCount > 0) {
      increaseAndUpdatePostViews(lastPostId, {} as IChannelPostDetails, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPostId, metadata]);

  if (
    !communityPostState?.hasMore &&
    !communityPostState?.loading &&
    communityPostState?.posts?.length === 0
  ) {
    return <NoDataFound title={`No post found`} />;
  }

  return (
    <div className="feed__content">
      <EasyVirtualized
        onLoadMore={loadMore}
        useParentScrollElement
        hasMore={communityPostState.hasMore}>
        {communityPostState.posts.map((item) => (
          <CommunityPostItem
            key={item._id}
            communityPostDetails={item}
            // index={index}
            // onCommentsPressed={() => {
            //   setSelectedPostId(item._id);
            //   setShowModal(true);
            // }}
          />
        ))}

        {userDetails.type !== 'creator_restricted' ? (
          communityPostState.loading || communityPostState.hasMore ? (
            <>
              <PostSkeleton />
              {/* <PostSkeleton /> */}
            </>
          ) : communityPostState.posts.length === 0 ? (
            noPostsMessage
          ) : (
            <Typography.Paragraph style={noPostsMessageStyle}>
              That’s it! No more posts to show.
            </Typography.Paragraph>
          )
        ) : null}
      </EasyVirtualized>
    </div>
  );
};

export default CommunityChannelPostList;
